import {
  AlertTriangle,
  Calculator,
  CheckCircle,
  Edit,
  Loader2,
  PenLine,
  RefreshCw,
  Save,
  X,
} from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ClosingDisclosure } from '../components/ClosingDisclosure';
import { PrintButton } from '../components/PrintPreview';
import { useCDForm } from '../hooks';
import {
  useRefreshCalculations,
  useSaveLineItems,
  useSaveLoanTerms,
  useSaveParties,
  useUpdateTransaction,
} from '../hooks/useCDMutations';
import { selectIsDirty, useClosingDisclosureStore } from '../store/closingDisclosureStore';
import type { ClosingDisclosureData } from '../types/closingDisclosureData';
import { createApiPayloadsFromFormChanges, type ServerData } from '../utils/transformData';
// Import print styles
import '../styles/print.css';

/**
 * TransactionPreviewPage - Preview a real transaction's Closing Disclosure
 *
 * Uses the unified useCDForm hook to load data into Zustand store,
 * which handles transformation from API/snapshot format to CD format.
 */
export default function TransactionPreviewPage() {
  const { id } = useParams<{ id: string }>();

  // Mutation hooks - automatically update store and invalidate cache
  const updateTransactionMutation = useUpdateTransaction();
  const saveLoanTermsMutation = useSaveLoanTerms();
  const savePartiesMutation = useSaveParties();
  const saveLineItemsMutation = useSaveLineItems();
  const refreshCalculations = useRefreshCalculations();

  // Use the unified hook for data loading and store hydration
  const { formData, isLoading, isError, error, isReady, loadingState } = useCDForm(id, 'preview');

  // Get server data and isDirty from store
  const serverData = useClosingDisclosureStore((state) => state.serverData);
  const isDirty = useClosingDisclosureStore(selectIsDirty);
  const transaction = serverData.transaction;

  // Edit mode state (local state - editing is a page-level concern)
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // Track missing data for user warning
  const missingData = useMemo(() => {
    if (!isReady) return [];
    const missing: string[] = [];
    if (!serverData.parties?.length) missing.push('parties');
    if (!serverData.lineItems?.length) missing.push('line items');
    if (!serverData.loanTerms) missing.push('loan terms');
    if (!serverData.calculations) missing.push('calculations');
    return missing;
  }, [isReady, serverData]);

  // Handle data changes from editable fields
  // Use getState() to access actions without subscribing to store updates
  // This prevents infinite loops when onChange triggers store updates
  const handleCDDataChange = useCallback((newData: ClosingDisclosureData) => {
    useClosingDisclosureStore.getState().setFormData(newData);
    setEditError(null);
    setSaveSuccess(false);
  }, []);

  // Enter edit mode
  const handleEditClick = useCallback(() => {
    setIsEditMode(true);
    setEditError(null);
    setSaveSuccess(false);
  }, []);

  // Cancel edit mode - revert to original data
  const handleCancelClick = useCallback(() => {
    useClosingDisclosureStore.getState().resetToServer();
    setIsEditMode(false);
    setEditError(null);
    setSaveSuccess(false);
  }, []);

  // Manual recalculate - refresh calculations from backend
  const handleRecalculate = useCallback(async () => {
    if (!id) return;

    setIsCalculating(true);
    try {
      await refreshCalculations(id, 2, 500);
      useClosingDisclosureStore.getState().syncFormDataFromServer();
    } catch (error) {
      console.error('Failed to refresh calculations:', error);
    } finally {
      setIsCalculating(false);
    }
  }, [id, refreshCalculations]);

  // Save changes - saves all entities (transaction, loan terms, parties, line items)
  // Uses mutation hooks which automatically update store and invalidate cache
  const handleSaveClick = useCallback(async () => {
    if (!id) return;

    setIsSaving(true);
    setEditError(null);
    setSaveSuccess(false);

    try {
      // Get the current server data from the store
      const currentServerData = useClosingDisclosureStore.getState().serverData;

      // Create API payloads by merging form changes into existing server data
      // This preserves all server-only fields (prepayment_penalty_details, etc.)
      const payloads = createApiPayloadsFromFormChanges(formData, currentServerData as ServerData);

      // Track save results for error reporting
      const saveResults: { entity: string; success: boolean; error?: string }[] = [];

      // 1. Save Transaction using mutation hook
      try {
        await updateTransactionMutation.mutateAsync({
          id: id,
          transaction: payloads.transaction,
        });
        saveResults.push({ entity: 'transaction', success: true });
      } catch (err) {
        saveResults.push({
          entity: 'transaction',
          success: false,
          error: err instanceof Error ? err.message : 'Failed to save transaction',
        });
      }

      // 2. Save Loan Terms (if present) using mutation hook
      if (payloads.loanTerms) {
        try {
          await saveLoanTermsMutation.mutateAsync({
            transactionId: id,
            loanTerms: payloads.loanTerms as Parameters<
              typeof saveLoanTermsMutation.mutateAsync
            >[0]['loanTerms'],
          });
          saveResults.push({ entity: 'loanTerms', success: true });
        } catch (err) {
          saveResults.push({
            entity: 'loanTerms',
            success: false,
            error: err instanceof Error ? err.message : 'Failed to save loan terms',
          });
        }
      }

      // 3. Save Parties (if present) using mutation hook
      if (payloads.parties && payloads.parties.length > 0) {
        // Filter out parties without names
        const validParties = payloads.parties.filter((p) => p.name && p.party_type);

        if (validParties.length > 0) {
          try {
            await savePartiesMutation.mutateAsync({
              transactionId: id,
              parties: validParties as Parameters<
                typeof savePartiesMutation.mutateAsync
              >[0]['parties'],
            });
            saveResults.push({ entity: 'parties', success: true });
          } catch (err) {
            saveResults.push({
              entity: 'parties',
              success: false,
              error: err instanceof Error ? err.message : 'Failed to save parties',
            });
          }
        }
      }

      // 4. Save Line Items (if present) using mutation hook
      if (payloads.lineItems && payloads.lineItems.length > 0) {
        try {
          await saveLineItemsMutation.mutateAsync({
            transactionId: id,
            items: payloads.lineItems as unknown as Parameters<
              typeof saveLineItemsMutation.mutateAsync
            >[0]['items'],
          });
          saveResults.push({ entity: 'lineItems', success: true });
        } catch (err) {
          saveResults.push({
            entity: 'lineItems',
            success: false,
            error: err instanceof Error ? err.message : 'Failed to save line items',
          });
        }
      }

      // Check if all saves were successful
      const failedSaves = saveResults.filter((r) => !r.success);
      if (failedSaves.length > 0) {
        const errorMessages = failedSaves.map((r) => `${r.entity}: ${r.error}`).join('; ');
        throw new Error(`Some changes failed to save: ${errorMessages}`);
      }

      // All saves successful - now refresh calculations ONCE with retry logic
      // This replaces the per-mutation calculations refresh that caused race conditions
      setIsSaving(false);
      setIsCalculating(true);
      try {
        await refreshCalculations(id, 2, 500);
      } catch {
        // Calculations refresh failed, but save was successful
        // The cache invalidation will trigger a refetch
      }
      setIsCalculating(false);

      // Sync formData from the updated serverData so the display reflects saved values
      useClosingDisclosureStore.getState().syncFormDataFromServer();

      // Show success message
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);

      // Exit edit mode
      setIsEditMode(false);
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to save changes. Please try again.';
      setEditError(errorMessage);
    } finally {
      setIsSaving(false);
      setIsCalculating(false);
    }
  }, [
    id,
    formData,
    updateTransactionMutation,
    saveLoanTermsMutation,
    savePartiesMutation,
    saveLineItemsMutation,
    refreshCalculations,
  ]);

  // Loading state
  if (isLoading || loadingState === 'loading') {
    return (
      <div className="min-h-screen bg-gray-100 py-8">
        <div className="max-w-[900px] mx-auto">
          <div className="flex flex-col justify-center items-center py-12 gap-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            <p className="text-gray-600">Loading transaction data...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (isError || loadingState === 'error') {
    return (
      <div className="min-h-screen bg-gray-100 py-8">
        <div className="max-w-[900px] mx-auto px-4">
          <Card className="border-red-200 bg-red-50">
            <CardContent className="py-6">
              <p className="text-red-800">{error?.message || 'Failed to load transaction data'}</p>
              <Button variant="outline" onClick={() => window.history.back()} className="mt-4">
                Go Back
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <div className="max-w-[900px] mx-auto px-4">
        <div className="mb-8 text-center cd-no-print">
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Closing Disclosure Preview</h1>
          <p className="text-gray-600 mb-2">
            {transaction?.property_address} - {transaction?.file_number}
          </p>

          {/* Status Indicators */}
          <div className="flex gap-2 justify-center mb-4">
            {isEditMode && isDirty && (
              <Badge variant="outline" className="border-amber-500 text-amber-700 bg-amber-50">
                <PenLine className="h-3 w-3 mr-1" />
                Unsaved Changes
              </Badge>
            )}
            {isCalculating && (
              <Badge variant="outline" className="border-blue-500 text-blue-700 bg-blue-50">
                <Calculator className="h-3 w-3 mr-1 animate-pulse" />
                Calculating...
              </Badge>
            )}
          </div>

          {/* Edit Mode Buttons */}
          <div className="flex gap-3 justify-center items-center flex-wrap">
            {!isEditMode ? (
              <>
                <Button variant="outline" onClick={() => window.history.back()}>
                  Back
                </Button>
                <Button
                  variant="default"
                  onClick={handleEditClick}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit Document
                </Button>
                <Button variant="outline" onClick={handleRecalculate} disabled={isCalculating}>
                  {isCalculating ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Calculating...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Recalculate
                    </>
                  )}
                </Button>
                <PrintButton
                  documentTitle={`Closing Disclosure - ${transaction?.file_number || 'Draft'}`}
                />
              </>
            ) : (
              <>
                <Button
                  variant="outline"
                  onClick={handleCancelClick}
                  disabled={isSaving || isCalculating}
                >
                  <X className="h-4 w-4 mr-2" />
                  Cancel
                </Button>
                <Button
                  variant="default"
                  onClick={handleSaveClick}
                  disabled={isSaving || isCalculating}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : isCalculating ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Calculating...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </Button>
              </>
            )}
          </div>

          {/* Success Message */}
          {saveSuccess && (
            <Alert className="mt-4 border-green-200 bg-green-50 max-w-md mx-auto">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">
                Changes saved successfully!
              </AlertDescription>
            </Alert>
          )}

          {/* Error Message */}
          {editError && (
            <Alert className="mt-4 border-red-200 bg-red-50 max-w-md mx-auto">
              <AlertDescription className="text-red-800">{editError}</AlertDescription>
            </Alert>
          )}

          {/* Missing Data Warning */}
          {missingData.length > 0 && (
            <Alert className="mt-4 border-yellow-200 bg-yellow-50 max-w-lg mx-auto">
              <AlertTriangle className="h-4 w-4 text-yellow-600" />
              <AlertDescription className="text-yellow-800">
                Some data is missing: {missingData.join(', ')}. The preview may be incomplete.
              </AlertDescription>
            </Alert>
          )}
        </div>

        <div className="bg-white p-4 shadow-lg">
          {/*
            Key is based on transaction ID + loading state + edit mode to force remount
            when data changes or edit mode toggles. This ensures ClosingDisclosureProvider
            initializes with fresh data.

            onChange is only passed when in edit mode to prevent infinite loops -
            ClosingDisclosureProvider calls onChange on initialization which would
            trigger store updates and re-renders in a loop.
          */}
          <ClosingDisclosure
            key={`cd-${id}-${loadingState}-${isEditMode}`}
            initialData={formData}
            editable={isEditMode}
            onChange={isEditMode ? handleCDDataChange : undefined}
            showPageSeparators={false}
          />
        </div>
      </div>
    </div>
  );
}
